import React, { Fragment, useContext, useState } from "react";
import { Table, Badge, Button, Tooltip } from "reactstrap";
import { FormattedNumber } from "../../../FormattedNumber/FormattedNumber";
import style from "./Bulk.module.scss";
import Edit from "./Edit";
import { Dispatch } from "../store/store";
import { Type, CommissionType } from "../../../../utils/Order/Order";
import CircularProgress from "../../../CircularProgress/CircularProgress";
import { toDateLocal, UserType } from "../../../../utils/Helper";
import translate from "../../../../utils/Translator";

const Bulk = ({
  canAddAccount,
  removeAccount,
  searchAccount,
  accountHolders,
  accountInfo,
  accounts,
  onChange,
  isReadOnly,
  canModifyComposition,
  readonly,
  balanceReady,
  account,
  error,
  orderId,
  hiddens,
}) => {
  const nextAccountIndex = accounts ? accounts.length : 0;

  const [edit, setEdit] = useState({
    open: account.index >= 0,
  });
  const [sending, setSending] = useState(false);

  const { dispatch, state, services } = useContext(Dispatch);
  const { ui, order } = state;

  const onSelectAccount = (accountIndex = null) => {
    dispatch({
      type: "SELECT_ACCOUNT",
      props: {
        accountIndex,
      },
    });
  };

  const toggleEdit = (accountIndex = null, discard) => {
    if (!edit.open) {
      if (canAddAccount()) {
        setEdit({ open: true });
        onSelectAccount(accountIndex);
      }
    } else {
      if (discard) {
        dispatch({ type: "DELETE_ACCOUNT", accountIndex });
      }

      setEdit({ open: false });
    }
  };

  const onEditAccount = (account) => {
    dispatch({
      type: "EDIT_ACCOUNT",
      props: {
        type: Type.Bulk,
        account,
      },
    });
  };

  const onDeleteAccount = (accountIndex, accountId) => {
    if (window.confirm(`Are you sure to remove the account?`)) {
      if (order.OrderId > 0) {
        removeAccount({
          orderId: order.OrderId,
          accountId,
        }).then((res) => {
          dispatch({ type: "DELETE_ACCOUNT", accountIndex });
        });
      } else {
        dispatch({ type: "DELETE_ACCOUNT", accountIndex });
      }
    }
  };

  const onDownloadFile = (accountId) => {
    const { downloadAttachment } = services.account;
    downloadAttachment({ orderId, accountId });
  };

  const onResendAuth = (accountId) => {
    const { resendAuth } = services.account;
    setSending(true);
    resendAuth(orderId, accountId);
    setTimeout(() => {
      setSending(false);
    }, 1000);
  };

  const { downloadAttachment, orderPdf } = services.account;
  const editProps = {
    toggle: toggleEdit,

    accountProps: {
      searchAccount,
      accountHolders,
      accountInfo,
      downloadAttachment,
    },
  };

  const orderCanResend = order.canResendAuthorizations;

  return (
    <div style={{}}>
      <span className={style.title}>Accounts</span>
      {edit.open ? (
        <Edit
          {...editProps}
          userType={ui.userRole}
          account={
            order.CurrentAccount || {
              index: nextAccountIndex,
              CommissionType: CommissionType.PrincipalPercentage,
              MarkupType: CommissionType.FaceValueCash,
              Advice: false,
              FXMarkupType: CommissionType.FaceValueCash,
              CurrentInfoDate: toDateLocal(new Date()),
            }
          }
          onChange={onChange}
          onEdit={onEditAccount}
          dispatch={dispatch}
          parameters={ui.parameters}
          accounts={order.Accounts}
          hiddens={hiddens}
          readonly={readonly}
          isReadOnly={isReadOnly}
          order={order}
          balanceReady={balanceReady}
          orderPdf={orderPdf}
        />
      ) : (
        <Fragment>
          <Button
            color="primary"
            className={`${style.btnAdd} btn-sm mt-1`}
            onClick={() => toggleEdit()}
            disabled={!canModifyComposition}
          >
            <i className="fa fa-plus" />
            {"\u00A0"} Add {translate("Account")}
          </Button>
          <div className={`${style.table} ${error ? style.error : ""}`}>
            <Table responsive striped>
              <thead>
                <tr>
                  <th>Quantity</th>
                  <th>{translate("Account")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {accounts.length == 0 && <EmptyAccounts />}
                {accounts.map((account, i) => {
                  const userIsOwner =
                    (ui.userRole == UserType.Trader && account.AccountType != "Customer") ||
                    (ui.userRole == UserType.Advisor && account.AccountType == "Customer");

                  return (
                    <AccountItem
                      key={`baccount-${i}`}
                      account={account}
                      readonly={!userIsOwner || !canModifyComposition}
                      onDelete={onDeleteAccount}
                      onEdit={() => toggleEdit(account.index)}
                      onDownloadFile={onDownloadFile}
                      orderCanResend={orderCanResend}
                      onResendAuth={onResendAuth}
                      sendingMail={sending}
                    />
                  );
                })}
              </tbody>
            </Table>
          </div>
          {error && <div className={`invalid-feedback ${style.errorText}`}>{error}</div>}
        </Fragment>
      )}
    </div>
  );
};

const AccountItem = ({
  account,
  readonly,
  onDelete,
  onEdit,
  onDownloadFile,
  onResendAuth,
  sendingMail,
  orderCanResend,
}) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const toggleTooltip = () => setTooltipOpen(!tooltipOpen);

  const accountCanResend = account.Instruction === "ViaLink";

  return (
    <tr>
      <td>
        <FormattedNumber value={account.Quantity} dataTypeDecimalName="quantity" />
      </td>
      <td>{account.AccountNumber}</td>
      {account.AccountId > 0 && (
        <td>
          {account.HasFile && (
            <Button
              color="btn-outline-primary btn-sm"
              disabled={account.HasFileUploadPending}
              onClick={() => onDownloadFile(account.AccountId)}
            >
              <i className="fa fa-download" />
            </Button>
          )}
          {orderCanResend && accountCanResend && (
            <React.Fragment>
              {sendingMail ? (
                <CircularProgress sm />
              ) : (
                <React.Fragment>
                  <Button
                    id="tooltipResend"
                    color="btn-outline-primary btn-sm"
                    onClick={() => onResendAuth(account.AccountId)}
                  >
                    <i className="fa fa-send" />
                  </Button>
                  <Tooltip placement="top" target="tooltipResend" isOpen={tooltipOpen} toggle={toggleTooltip}>
                    Resend Authorization Mail
                  </Tooltip>
                </React.Fragment>
              )}
            </React.Fragment>
          )}
          <Button color="btn-outline-primary btn-sm" onClick={() => onEdit()}>
            <i className="fa fa-pencil" />
          </Button>
          <Button
            color="btn-outline-primary btn-sm"
            disabled={readonly}
            onClick={() => onDelete(account.index, account.AccountId)}
            style={{ color: "#d83737" }}
          >
            <i className="fa fa-trash" />
          </Button>
        </td>
      )}
    </tr>
  );
};

const EmptyAccounts = () => (
  <tr className={style.empty}>
    <td colSpan="3">
      <h5 style={{ textAlign: "center" }}>No {translate("Account")}s</h5>
    </td>
  </tr>
);

export default Bulk;
